import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FiFileText, FiUser, FiClock, FiInbox, FiSearch } from "react-icons/fi";
import { CircularProgress } from "@mui/material";
import { API_URL } from '../config/api';

const UploadedFilesList = () => {
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 12;

	useEffect(() => {
		const fetchFiles = async () => {
			const token = localStorage.getItem("token");
			try {
				const res = await axios.get(`${API_URL}/send.php`, {
					headers: { Authorization: `Bearer ${token}` },
				});

				if (res.data.status && Array.isArray(res.data.data)) {
					setFiles(res.data.data);
				} else {
					setFiles([]);
					if (!res.data.status && res.data.message) {
						setError(res.data.message);
					}
				}
			} catch (err) {
				setError("Failed to load uploaded files. Please try again later.");
			} finally {
				setLoading(false);
			}
		};

		fetchFiles();
		const interval = setInterval(fetchFiles, 5000);
		return () => clearInterval(interval);
	}, []);

	// Filter + Pagination
	const filteredFiles = files.filter((file) => {
		const nameMatch = file.file_name
			?.toLowerCase()
			.includes(searchQuery.toLowerCase());

		// Date filtering
		let dateMatch = true;
		if (fromDate && toDate) {
			const fileDate = new Date(file.created_at).toISOString().split("T")[0];
			dateMatch = fileDate >= fromDate && fileDate <= toDate;
		} else if (fromDate) {
			const fileDate = new Date(file.created_at).toISOString().split("T")[0];
			dateMatch = fileDate >= fromDate;
		} else if (toDate) {
			const fileDate = new Date(file.created_at).toISOString().split("T")[0];
			dateMatch = fileDate <= toDate;
		}

		return nameMatch && dateMatch;
	});

	const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
	const paginatedFiles = filteredFiles.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage
	);

	// Reset to first page on filter change
	useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery, fromDate, toDate]);

	if (loading) {
		return (
			<div className='text-center py-20 text-gray-500 flex flex-col items-center justify-center'>
				<CircularProgress color='inherit' size={40} className='mb-3' />
				<p className='text-lg'>Loading uploaded files...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className='text-center text-red-600 py-20 font-medium text-lg'>
				<p>{error}</p>
			</div>
		);
	}

	return (
		<div className='max-w-7xl mx-auto px-4 py-10'>
			<h2 className='text-3xl font-bold text-center text-red-700 mb-8'>
				Recently Uploaded Files
			</h2>

			{/* Filters */}
			<div className='flex flex-col md:flex-row gap-4 justify-between items-center mb-8'>
				<div className='flex items-center w-full md:w-1/2 bg-white px-4 py-2 rounded-md shadow border border-red-200'>
					<FiSearch className='text-red-500 mr-2 text-lg' />
					<input
						type='text'
						placeholder='Search by file name...'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className='w-full bg-transparent focus:outline-none text-sm text-gray-800'
					/>
				</div>

				<div className='flex gap-2 items-center'>
					<input
						type='date'
						value={fromDate}
						onChange={(e) => setFromDate(e.target.value)}
						className='px-4 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-400'
					/>
					<span className='text-gray-600'>to</span>
					<input
						type='date'
						value={toDate}
						onChange={(e) => setToDate(e.target.value)}
						className='px-4 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-400'
					/>
				</div>
			</div>

			{/* File Cards */}
			{paginatedFiles.length === 0 ? (
				<div className='text-center text-gray-400 py-16'>
					<FiInbox className='mx-auto text-5xl mb-3 text-red-400' />
					<p className='text-lg font-medium text-gray-600'>
						No files match your search
					</p>
				</div>
			) : (
				<>
					<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
						{paginatedFiles.map((file) => {
							const cardStyle =
								"bg-white p-5 rounded-lg border shadow-sm border-red-200 hover:shadow-md hover:border-red-400 transition-all duration-200 ease-in-out block";

							return (
								<Link
									to={`/dashboard/summary`}
									state={{ file }}
									className={cardStyle}
									key={file.id}
								>
									<div className='flex items-start justify-between gap-3 mb-4'>
										<div className='flex items-center gap-3 min-w-0 flex-grow'>
											<FiFileText className='text-red-600 text-2xl flex-shrink-0' />
											<h3 className='font-semibold text-lg text-gray-800 truncate'>
												{file.file_name || "Untitled.csv"}
											</h3>
										</div>
									</div>
									<div className='text-sm text-gray-700 space-y-1'>
										<p>
											<span className='font-medium text-red-700'>Type:</span>{" "}
											{file.type || "General"}
										</p>
										<p>
											<span className='font-medium text-red-700'>
												Batch ID:
											</span>{" "}
											{file.batch_id}
										</p>
										<p>
											<span className='font-medium text-red-700'>
												Total Contacts:
											</span>{" "}
											{Number(file.total_count || 0).toLocaleString()}
										</p>
										<p>
											<span className='font-medium text-red-700'>
												Distinct Contacts:
											</span>{" "}
											{Number(file.total_distinct || 0).toLocaleString()}
										</p>
										<p className='break-words'>
											<span className='font-medium text-red-700'>Message:</span>{" "}
											{file.message?.slice(0, 100)}
											{file.message && file.message.length > 100
												? "..."
												: "" || "N/A"}
										</p>
									</div>
									<div className='flex justify-between items-center text-xs text-gray-500 mt-4'>
										<span className='flex items-center gap-1 text-red-600'>
											<FiUser /> {file.full_name || "Unknown"}
										</span>
										<span className='flex items-center gap-1 text-gray-500'>
											<FiClock /> {file.created_at}
										</span>
									</div>
								</Link>
							);
						})}
					</div>

					{/* Pagination Controls */}
					{totalPages > 1 && (
						<div className='mt-6 flex justify-center items-center gap-3'>
							<button
								onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
								disabled={currentPage === 1}
								className='px-3 py-1 border rounded text-sm bg-red-50 border-red-300 text-red-600 hover:bg-red-100 disabled:opacity-50'
							>
								Previous
							</button>
							<span className='text-sm text-gray-700'>
								Page {currentPage} of {totalPages}
							</span>
							<button
								onClick={() =>
									setCurrentPage((prev) => Math.min(prev + 1, totalPages))
								}
								disabled={currentPage === totalPages}
								className='px-3 py-1 border rounded text-sm bg-red-50 border-red-300 text-red-600 hover:bg-red-100 disabled:opacity-50'
							>
								Next
							</button>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default UploadedFilesList;
